import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import CoachLayout from "@/components/CoachLayout";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ChevronRight, AlertTriangle, Power, Trash2, Loader2, CalendarClock, Video, Phone, User } from "lucide-react";
import { toast } from "sonner";

const STATUS = {
  normal: { label: "évolution normale", dot: "bg-[#4A6B53]", text: "text-[#4A6B53]" },
  surveiller: { label: "à surveiller", dot: "bg-[#8C6B16]", text: "text-[#8C6B16]" },
  attention: { label: "attention recommandée", dot: "bg-destructive", text: "text-destructive" },
};

export default function CoachDashboard() {
  const [data, setData] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [acting, setActing] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const load = () => api.get("/coach/clients").then((r) => setData(r.data));
  useEffect(() => {
    load();
    api.get("/coach/alerts").then((r) => setAlerts(r.data));
    api.get("/coach/bookings").then((r) => setBookings(r.data.bookings || [])).catch(() => {});
  }, []);

  const errMsg = (err, fb) => {
    const d = err?.response?.data?.detail;
    toast.error(typeof d === "string" ? d : fb);
  };

  const toggleClient = async (c) => {
    setActing(c.user_id);
    try {
      await api.patch(`/admin/clients/${c.user_id}/status`, { disabled: !c.disabled });
      toast.success(c.disabled ? "Client réactivé" : "Client désactivé");
      load();
    } catch (err) { errMsg(err, "Action impossible"); } finally { setActing(null); }
  };

  const removeClient = async (c) => {
    if (!window.confirm(`Supprimer définitivement ${c.name || "ce client"} et toutes ses données ? Cette action est irréversible.`)) return;
    setActing(c.user_id);
    try {
      const res = await api.delete(`/admin/clients/${c.user_id}`);
      const purged = res.data?.collections_purged || {};
      const accounts = (res.data?.deleted || []).length || 1;
      const parts = [];
      if (purged.sessions) parts.push(`${purged.sessions} séance(s)`);
      if (purged.programs) parts.push(`${purged.programs} programme(s)`);
      if (purged.bilans) parts.push(`${purged.bilans} bilan(s)`);
      if (purged.payment_transactions) parts.push(`${purged.payment_transactions} paiement(s)`);
      const totalColl = Object.values(purged).reduce((a, b) => a + b, 0);
      const detail = parts.length
        ? `${parts.join(", ")} — ${totalColl} entrée(s) au total supprimées définitivement.`
        : `Aucune donnée résiduelle. Suppression définitive effectuée.`;
      toast.success(`Client supprimé${accounts > 1 ? ` (${accounts} comptes du même email)` : ""}`, { description: detail });
      load();
    } catch (err) { errMsg(err, "Suppression impossible"); } finally { setActing(null); }
  };

  if (!data) return <CoachLayout><div className="pulse-organic w-3 h-3 rounded-full bg-obsidian mx-auto mt-20" /></CoachLayout>;

  const order = { attention: 0, surveiller: 1, normal: 2 };
  const clients = [...data.clients].sort((a, b) => order[a.status] - order[b.status]);

  return (
    <CoachLayout>
      <div className="fade-up">
        <div className="flex items-end justify-between">
          <div>
            <div className="font-display text-6xl font-light tabnum leading-none">{data.total}</div>
            <div className="mt-2 text-sm tracking-[0.16em] uppercase text-obsidian/50">clients actifs</div>
          </div>
          <div className="flex gap-6 text-right">
            <Count n={data.counts.normal} l="normal" c="text-[#4A6B53]" />
            <Count n={data.counts.surveiller} l="à surveiller" c="text-[#8C6B16]" />
            <Count n={data.counts.attention} l="attention" c="text-destructive" />
          </div>
        </div>

        {alerts.length > 0 && (
          <div className="mt-8 border border-destructive/30 bg-destructive/5 p-4 flex items-start gap-3">
            <AlertTriangle size={18} className="text-destructive shrink-0 mt-0.5" />
            <div className="text-sm">
              <span className="font-medium">{alerts.length} situation(s) remontée(s)</span> par le moteur — adaptations majeures ou signaux de feedback à examiner.
            </div>
          </div>
        )}

        {bookings.length > 0 && (
          <div className="mt-8" data-testid="coach-upcoming-rdv">
            <div className="text-sm tracking-[0.16em] uppercase font-semibold text-obsidian/50 flex items-center gap-2 mb-3"><CalendarClock size={16} /> Prochains rendez-vous ({bookings.length})</div>
            <div className="grid sm:grid-cols-2 gap-3">
              {bookings.map((b) => (
                <div key={b.booking_id} data-testid={`coach-rdv-${b.booking_id}`} className="rounded-xl border border-border bg-white p-4">
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-sm font-medium flex items-center gap-1.5"><User size={14} className="text-sage" /> {b.client_name || "Client"}</div>
                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ${b.mode === "phone" ? "bg-obsidian/8 text-obsidian/70" : "bg-sage/15 text-sage"}`}>
                      {b.mode === "phone" ? <><Phone size={9} /> Tél</> : <><Video size={9} /> Visio</>}
                    </span>
                  </div>
                  <div className="mt-1 text-sm text-obsidian/70">{b.slot_label}</div>
                  {b.offer_label && <div className="text-xs text-obsidian/50 mt-0.5">{b.offer_label}</div>}
                  {b.phone && <a href={`tel:${b.phone}`} className="mt-1.5 inline-flex items-center gap-1 text-xs text-obsidian/60 hover:text-obsidian"><Phone size={11} /> {b.phone}</a>}
                  {b.note && <div className="mt-1.5 text-xs text-obsidian/45 italic">« {b.note} »</div>}
                  {b.mode !== "phone" && b.meet_link && (
                    <a data-testid={`coach-rdv-meet-${b.booking_id}`} href={b.meet_link} target="_blank" rel="noreferrer"
                      className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-obsidian text-white px-3 py-1.5 text-xs font-medium hover:-translate-y-0.5 transition-transform"><Video size={12} /> Rejoindre la visio</a>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-10 overflow-x-auto no-scrollbar -mx-4 sm:mx-0 px-4 sm:px-0">
         <div className="min-w-[720px] border-t border-l border-border">
          <div className="grid grid-cols-12 bg-obsidian text-white text-[11px] tracking-[0.14em] uppercase">
            <div className="col-span-4 px-4 py-3 border-r border-white/10">Client</div>
            <div className="col-span-3 px-4 py-3 border-r border-white/10">Objectif</div>
            <div className="col-span-2 px-4 py-3 border-r border-white/10">Régularité</div>
            <div className={`${isAdmin ? "col-span-2" : "col-span-3"} px-4 py-3 border-r border-white/10`}>Statut</div>
            {isAdmin && <div className="col-span-1 px-4 py-3">Actions</div>}
          </div>
          {clients.map((c) => (
            <div key={c.user_id} data-testid={`coach-client-row-${c.user_id}`}
              className={`grid grid-cols-12 items-center bg-white border-b border-r border-border ${c.disabled ? "opacity-60" : ""}`}>
              <button onClick={() => navigate(`/coach/client/${c.user_id}`)}
                className="col-span-4 px-4 py-3.5 border-r border-border flex items-center gap-3 text-left hover:bg-secondary/60 transition-[background-color]">
                {c.picture ? <img src={c.picture} alt="" className="w-8 h-8 rounded-full object-cover bg-secondary" />
                  : <span className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center text-xs font-medium">{(c.name || "?").charAt(0)}</span>}
                <span className="text-sm font-medium truncate">{c.name}</span>
                {c.is_demo && <span className="text-[9px] tracking-wide uppercase bg-secondary text-obsidian/50 px-1.5 py-0.5 rounded">démo</span>}
                {c.disabled && <span data-testid={`client-disabled-${c.user_id}`} className="text-[9px] tracking-wide uppercase bg-destructive/10 text-destructive px-1.5 py-0.5 rounded">désactivé</span>}
              </button>
              <div className="col-span-3 px-4 py-3.5 border-r border-border text-sm text-obsidian/70 truncate">{c.goal || "—"}</div>
              <div className="col-span-2 px-4 py-3.5 border-r border-border text-sm tabnum">{Math.round(c.regularity * 100)}%</div>
              <div className={`${isAdmin ? "col-span-2" : "col-span-3"} px-4 py-3.5 border-r border-border flex items-center justify-between`}>
                <span className={`inline-flex items-center gap-2 text-xs ${STATUS[c.status].text}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${STATUS[c.status].dot}`} /> {STATUS[c.status].label}
                </span>
                {!isAdmin && <ChevronRight size={16} className="text-obsidian/30" />}
              </div>
              {isAdmin && (
                <div className="col-span-1 px-2 py-3.5 flex items-center justify-center gap-0.5">
                  <button data-testid={`client-toggle-${c.user_id}`} onClick={() => toggleClient(c)} disabled={acting === c.user_id}
                    title={c.disabled ? "Réactiver" : "Désactiver"}
                    className={`p-1.5 rounded-sm hover:bg-secondary transition-[background-color] disabled:opacity-40 ${c.disabled ? "text-sage" : "text-obsidian/60"}`}>
                    {acting === c.user_id ? <Loader2 size={14} className="animate-spin" /> : <Power size={14} />}
                  </button>
                  <button data-testid={`client-delete-${c.user_id}`} onClick={() => removeClient(c)} disabled={acting === c.user_id}
                    title="Supprimer" className="p-1.5 rounded-sm text-destructive hover:bg-destructive/10 transition-[background-color] disabled:opacity-40">
                    <Trash2 size={14} />
                  </button>
                </div>
              )}
            </div>
          ))}
         </div>
        </div>
        <p className="mt-6 text-xs text-obsidian/40">Le moteur analyse automatiquement les dossiers. Inutile d'inspecter chaque client chaque semaine.</p>
      </div>
    </CoachLayout>
  );
}

function Count({ n, l, c }) {
  return (<div><div className={`font-display text-2xl font-light tabnum ${c}`}>{n}</div><div className="text-[11px] text-obsidian/50 mt-0.5">{l}</div></div>);
}
